/**
 * Styled XLSX export for a single item's detail page.
 *
 * Mirrors `apps/web/src/features/invoices/list/list-export.ts`:
 *   • Row 1 — merged title.
 *   • Rows 2–5 — info block (item, SKU, generated).
 *   • Row 7 — frozen header (bold, light-grey fill).
 *   • Rows 8+ — Field / Value pairs, with money as real INR numbers,
 *     GST as `%`, dates as real `Date` values so Excel can sort.
 *
 * ExcelJS is ~700 KB so we `await import` it — only users who click Export
 * pay the download.
 */
import type { items as itemsContract } from "@flint/api-contract";
import type { z } from "zod";

type ItemRow = z.infer<typeof itemsContract.itemRow>;

const INR_FORMAT = '"₹"#,##,##0.00;"-₹"#,##,##0.00';
const DATE_FORMAT = "yyyy-mm-dd";

const COLUMN_WIDTHS = [28, 44] as const;

function paiseToRupees(paise: string | null): number | null {
  if (!paise) return null;
  return Number(BigInt(paise)) / 100;
}

function capitalize(s: string): string {
  return s[0]!.toUpperCase() + s.slice(1);
}

function labelPreference(p: ItemRow["taxPreference"]): string {
  switch (p) {
    case "taxable": return "Taxable";
    case "exempt": return "Exempt";
    case "nil_rated": return "Nil-rated";
    case "non_gst": return "Non-GST";
  }
}

type CellValue = string | number | Date | null;
interface FieldRow {
  label: string;
  value: CellValue;
  numFmt?: string;
}

function buildRows(item: ItemRow): FieldRow[] {
  const rate = paiseToRupees(item.defaultRatePaise);
  const cost = paiseToRupees(item.lastCostPaise);
  const mrp = paiseToRupees(item.mrpPaise);
  const gstLabel =
    item.taxPreference === "taxable"
      ? item.defaultTaxRateBps !== null
        ? `${item.defaultTaxRateBps / 100}%`
        : null
      : labelPreference(item.taxPreference);
  const created = new Date(item.createdAt);
  const updated = item.updatedAt ? new Date(item.updatedAt) : null;

  return [
    { label: "Name", value: item.name },
    { label: "SKU", value: item.sku ?? "" },
    { label: "HSN/SAC", value: item.hsn ?? "" },
    { label: "Type", value: capitalize(item.itemType) },
    { label: "Usage", value: capitalize(item.usage) },
    { label: "Category", value: item.category ?? "" },
    { label: "Brand", value: item.brand ?? "" },
    { label: "Manufacturer", value: item.manufacturer ?? "" },
    { label: "Unit", value: item.unit },
    { label: "Rate (INR)", value: rate, numFmt: INR_FORMAT },
    { label: "Last cost (INR)", value: cost, numFmt: INR_FORMAT },
    { label: "MRP (INR)", value: mrp, numFmt: INR_FORMAT },
    { label: "GST", value: gstLabel },
    { label: "Tracking", value: item.trackStock ? "on" : "off" },
    { label: "Reorder level", value: item.reorderLevel ?? null },
    { label: "Lead time (days)", value: item.leadTimeDays ?? null },
    { label: "MOQ", value: item.minOrderQty ?? null },
    { label: "Preferred vendor", value: item.preferredVendorName ?? "" },
    { label: "Tags", value: item.tags.join("; ") },
    { label: "Description", value: item.description ?? "" },
    { label: "Created", value: !Number.isNaN(created.getTime()) ? created : null, numFmt: DATE_FORMAT },
    { label: "Updated", value: updated && !Number.isNaN(updated.getTime()) ? updated : null, numFmt: DATE_FORMAT },
  ];
}

/**
 * Build an `.xlsx` blob for a single item's detail and trigger the download.
 * Filename embeds the SKU (or item id if no SKU) + today.
 */
export async function downloadItemXlsx(item: ItemRow): Promise<void> {
  const ExcelJS = (await import("exceljs")).default;

  const wb = new ExcelJS.Workbook();
  wb.creator = "Flint";
  wb.created = new Date();
  const ws = wb.addWorksheet("Item", {
    // Freeze at row 7 so the info block + header row stay visible.
    views: [{ state: "frozen", ySplit: 7 }],
  });

  ws.columns = COLUMN_WIDTHS.map((width) => ({ width }));

  // ── Row 1 — Title ──────────────────────────────────────────────────
  ws.mergeCells("A1:B1");
  const title = ws.getCell("A1");
  title.value = `Item — ${item.name}`;
  title.font = { name: "Calibri", size: 16, bold: true, color: { argb: "FF0F172A" } };
  title.alignment = { vertical: "middle", horizontal: "left" };
  ws.getRow(1).height = 28;

  // ── Rows 2-5 — Info block ─────────────────────────────────────────
  const infoRows: Array<[string, string]> = [
    ["Item id", item.id],
    ["SKU", item.sku ?? "—"],
    ["Type", `${capitalize(item.itemType)} · ${capitalize(item.usage)}`],
    ["Generated", new Date().toISOString().slice(0, 10)],
  ];
  infoRows.forEach(([label, value], i) => {
    const row = ws.getRow(2 + i);
    row.getCell(1).value = label;
    row.getCell(1).font = { bold: true, color: { argb: "FF475569" } };
    row.getCell(2).value = value;
    if (label === "Item id" || label === "SKU") {
      row.getCell(2).font = { name: "Consolas", family: 3 };
      row.getCell(2).numFmt = "@"; // text — no scientific notation on numeric-looking ids
    }
  });

  // Row 6 intentionally blank for breathing room before the header.

  // ── Row 7 — Data header ────────────────────────────────────────────
  const headerRow = ws.getRow(7);
  ["Field", "Value"].forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.font = { bold: true, color: { argb: "FF0F172A" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF1F5F9" },
    };
    cell.alignment = { vertical: "middle", horizontal: "left" };
    cell.border = { bottom: { style: "thin", color: { argb: "FF94A3B8" } } };
  });
  headerRow.height = 22;

  // ── Rows 8+ — Data ─────────────────────────────────────────────────
  const rows = buildRows(item);
  rows.forEach((r, i) => {
    const rowNum = 8 + i;
    const row = ws.getRow(rowNum);
    row.getCell(1).value = r.label;
    row.getCell(1).font = { color: { argb: "FF475569" } };
    if (r.value === null || r.value === "") {
      row.getCell(2).value = "—";
      row.getCell(2).font = { color: { argb: "FF94A3B8" } };
    } else {
      row.getCell(2).value = r.value;
      if (r.numFmt) row.getCell(2).numFmt = r.numFmt;
    }
  });

  // ── Download ──────────────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  const safeName = (item.sku ?? item.id).replace(/[^\w.-]+/g, "_");
  anchor.download = `item-${safeName}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
